import {
  Box,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import ChargeStatistics from 'components/card/ChargeStatistics';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import useAuth from 'contexts/useAuth';
import {
  MdAccountBalance,
  MdAttachMoney,
  MdBarChart,
  MdBlock,
  MdCancel,
  MdCheck,
  MdOutlineAutorenew,
  MdPendingActions,
  MdPix,
  MdReceipt,
  MdSell,
  MdTaskAlt,
} from 'react-icons/md';
import PullToRefresh from 'react-simple-pull-to-refresh';

import Card from 'components/card/Card';
import { BsCash, BsCreditCard, BsCreditCardFill } from 'react-icons/bs';
import { dashboardCustomer, findAllTerminals, findTerminalsApp } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import WeeklyRevenue from 'views/access/default/components/WeeklyRevenue';

import CustomButton from 'components/CustomButton';
import FormRemoteSelectInput, { SelectOptionType } from 'components/FormRemoteSelectInput';
import InputForm from 'components/InputForm';
import SelectForm from 'components/SelectForm';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaBarcode, FaFileInvoiceDollar } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { IoCashOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import CreateCharge from './CreateCharge';
import ConfigureAccount from './ConfigureAccount';

interface DashboardForm {
  type?: string;
  initialDate: string;
  finalDate: string;
  company: SelectOptionType;
  customer: SelectOptionType;
}

export default function UserReports() {
  const toast = useToast();
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(false);

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };
  const { userData, updateUserData } = useAuth();
  const [filter, setFilter] = useState<{
    type?: string;
    initialDate: string;
    finalDate: string;
    companyId?: string;
    customerId?: string;
  }>({
    finalDate: DateTime.local().endOf('month').toFormat("yyyy-MM-dd'T'hh:mm"),
    initialDate: DateTime.local().startOf('day').toFormat("yyyy-MM-dd'T'HH:mm"),
  });

  const { control, handleSubmit } = useForm<DashboardForm>({
    defaultValues: {
      finalDate: DateTime.local().endOf('month').toFormat("yyyy-MM-dd'T'hh:mm"),
      initialDate: DateTime.local().startOf('day').toFormat("yyyy-MM-dd'T'HH:mm"),
    },
  });

  const { data, refetch } = useQuery(['dashboard', filter], () => dashboardCustomer(filter), {
    enabled: userData.isAuth === true,
  });

  const buscar = (data: DashboardForm) => {
    if (DateTime.fromISO(data.initialDate) > DateTime.fromISO(data.finalDate)) {
      toast({
        title: 'Erro',
        description: 'A data inicial não pode ser maior que a data final',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    if (DateTime.fromISO(data.initialDate).diff(DateTime.fromISO(data.finalDate)).as('days') < -180) {
      toast({
        title: 'Erro',
        description: 'A diferença entre as datas não pode ser maior que 180 dias',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    const search = {
      type: data.type,
      initialDate: data.initialDate,
      finalDate: data.finalDate,
      companyId: data.company?.value || undefined,
      customerId: data.customer?.value || undefined,
    };
    if (search !== filter) {
      refetch();
    }
    setFilter(search);
  };

  const brandColor = useColorModeValue('brand.900', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'navy.800');

  const percenteProfit = data?.pastMonthLiquid ? (data?.amount / (data?.pastMonthLiquid || 1) - 1) * 100 : 0;

  const handleRefresh = async () => {
    await refetch();
    updateUserData();
  };

  const quickActions = [
    {
      label: 'Pix',
      icon: MdPix,
      color: 'teal.500',
      path: '/access/pix',
      enabled: userData.features?.pix,
    },
    {
      label: 'Extrato',
      icon: MdReceipt,
      color: 'blue.500',
      path: '/access/balances',
      enabled: true,
    },
    {
      label: 'Cobranças',
      icon: FaFileInvoiceDollar,
      color: 'purple.500',
      path: '/access/charges',
      enabled: true,
    },
    {
      label: 'Saques',
      icon: IoCashOutline,
      color: 'orange.500',
      path: '/access/saques',
      enabled: userData.features?.withdraw,
    },
    {
      label: 'Vendas',
      icon: MdBarChart,
      color: 'green.500',
      path: '/access/transactions',
      enabled: true,
    },
  ];

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <Box>
        {/* ===== SEÇÃO 1: Dados da Conta ===== */}
        <Card
          bg={brandColor}
          color="white"
          p={6}
          mb={4}
          borderRadius="xl"
        >
          <Flex align="center" gap={3} mb={3}>
            <Icon as={MdAccountBalance} w="28px" h="28px" />
            <Box>
              <Text fontSize="lg" fontWeight="700">
                {userData.name}
              </Text>
              <Text fontSize="sm" opacity={0.8}>
                Conta: {userData.accountNumber || '—'}
              </Text>
            </Box>
          </Flex>

          <Divider borderColor="whiteAlpha.400" my={3} />

          {/* ===== SEÇÃO 2: Saldo e Saldo Bloqueado ===== */}
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">
                Saldo disponível
              </Text>
              <Text fontSize="2xl" fontWeight="700">
                {maskCurrency(userData.balance || 0)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">
                Saldo bloqueado
              </Text>
              <Text fontSize="2xl" fontWeight="700">
                {maskCurrency(userData.amountLocked || 0)}
              </Text>
            </Box>
          </SimpleGrid>

          {userData.balancePending > 0 && (
            <Box mt={2}>
              <Text fontSize="xs" opacity={0.7} textTransform="uppercase">
                Saldo pendente
              </Text>
              <Text fontSize="lg" fontWeight="600">
                {maskCurrency(userData.balancePending || 0)}
              </Text>
            </Box>
          )}
        </Card>

        {/* ===== SEÇÃO 3: Botões de Ações Rápidas (estilo banco) ===== */}
        <CreateCharge />

        <HStack my={4}>
          <ConfigureAccount />
        </HStack>

        <Flex
          overflowX="auto"
          gap={3}
          mb={5}
          pb={2}
          css={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {quickActions
            .filter((a) => a.enabled !== false)
            .map((action) => (
              <VStack
                key={action.label}
                minW="72px"
                cursor="pointer"
                onClick={() => navigate(action.path)}
                spacing={1}
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
              >
                <Flex
                  w="56px"
                  h="56px"
                  bg={cardBg}
                  borderRadius="full"
                  align="center"
                  justify="center"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                >
                  <Icon as={action.icon} w="24px" h="24px" color={action.color} />
                </Flex>
                <Text fontSize="xs" fontWeight="600" textAlign="center" color="gray.700">
                  {action.label}
                </Text>
              </VStack>
            ))}
        </Flex>

        {/* ===== SEÇÃO 4: Resumo de Vendas (entradas/saídas) ===== */}

        {showFilters ? (
          <SimpleGrid
            mb={3}
            columns={{ base: 2, md: 3, xl: 4 }}
            spacing={{ base: 3, md: 3, xl: 4 }}
            alignItems="center"
          >
            <SelectForm
              control={control}
              name="type"
              placeholder="Forma de Pagamento"
              label="Tipo da venda:"
              options={[
                { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
                { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
                { value: 'PIX', label: 'Pix' },
                { value: 'TICKET', label: 'Boleto' },
                { value: 'CASH', label: 'Dinheiro' },
                { value: 'VOUCHER', label: 'Voucher' },
              ]}
            />
            <InputForm
              control={control}
              name="initialDate"
              label="Data inicial:"
              rules={{ required: true }}
              type="datetime-local"
              placeholder="Data inicial"
            />
            <InputForm
              control={control}
              name="finalDate"
              label="Data final:"
              rules={{ required: true }}
              type="datetime-local"
              placeholder="Data final"
            />

            {
              <FormRemoteSelectInput
                control={control}
                name="maquineta"
                label="Terminal"
                size="sm"
                onChange={(e) => {
                  setFilter((prev) => ({ ...prev, maquinetaId: e.value }));
                }}
                loadDataFn={({ value, cb }) =>
                  findAllTerminals({
                    filter: value,
                    limit: 10,
                    page: 0,
                    orderBy: [
                      {
                        id: 'name',
                        desc: false,
                      },
                    ],
                  }).then((retorno) => {
                    if (retorno?.registers?.length > 0) {
                      cb([
                        { label: 'Escolher uma opção...', value: '' },
                        ...retorno.registers?.map((d: any) => ({
                          label: `${d.number} - ${d.name}`,
                          value: d.id,
                        })),
                      ]);
                    } else {
                      cb([]);
                    }
                  })
                }
              />
            }
            <FormRemoteSelectInput
              control={control}
              name="promoter"
              label="Promoter"
              size="sm"
              onChange={(e) => {
                setFilter((prev) => ({
                  ...prev,
                  promoterId: e.value,
                }));
              }}
              loadDataFn={({ value, cb }) =>
                findTerminalsApp({
                  filter: value,
                  limit: 10,
                  page: 0,
                  orderBy: [
                    {
                      id: 'name',
                      desc: false,
                    },
                  ],
                }).then((retorno) => {
                  if (retorno?.registers?.length > 0) {
                    cb([
                      { label: 'Escolher uma opção...', value: '' },
                      ...retorno.registers?.map((d: any) => ({
                        label: d.name,
                        value: d.id,
                      })),
                    ]);
                  } else {
                    cb([]);
                  }
                })
              }
            />
            <CustomButton
              variant="solid"
              colorScheme="brand"
              onClick={handleSubmit(buscar)}
            >
              Buscar
            </CustomButton>
            <CustomButton
              variant="ghost"
              onClick={toggleFilters}
            >
              Fechar filtros
            </CustomButton>
          </SimpleGrid>
        ) : (
          <SimpleGrid
            mb={3}
            columns={{ base: 1, md: 3, xl: 4 }}
            spacing={{ base: 3, md: 3, xl: 3 }}
            alignItems="center"
          >
            <CustomButton
              colorScheme="brand"
              onClick={toggleFilters}
              leftIcon={<FiFilter />}
            >
              Filtrar
            </CustomButton>
          </SimpleGrid>
        )}

        {/* Resumo de Cobranças */}
        <Text fontSize="md" fontWeight="700" mb="5px" ml={5}>
          Resumo de Cobranças
        </Text>
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 4, '2xl': 4 }}
          gap="20px"
          mb="20px"
        >
          <ChargeStatistics
            startContent={
              <IconBox w="56px" h="56px" bg={boxBg} icon={<Icon w="32px" h="32px" as={MdCheck} color="green.600" />} />
            }
            name="Finalizadas"
            value={maskCurrency(data?.charges?.totalPaids)}
            growth={data?.charges?.paids}
          />
          <ChargeStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdPendingActions} color="yellow.400" />}
              />
            }
            name="Pendentes"
            value={maskCurrency(data?.charges?.totalPending)}
            growth={data?.charges?.pending}
          />
          <ChargeStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdCancel} color={'red.400'} />}
              />
            }
            name="Canceladas"
            value={maskCurrency(data?.charges?.totalCanceled)}
            growth={data?.charges?.canceleds}
          />
          <ChargeStatistics
            startContent={
              <IconBox
                w="56px"
                h="56px"
                bg={boxBg}
                icon={<Icon w="32px" h="32px" as={MdOutlineAutorenew} color={brandColor} />}
              />
            }
            name="Devoluções"
            value={maskCurrency(data?.charges?.refunds)}
          />
        </SimpleGrid>

        {/* Resumo de Vendas */}
        <Flex direction="column" flex="4" gap="20px">
          <Box>
            <Text fontSize="md" fontWeight="700" mb="5px" ml={5}>
              Resumo de Vendas
            </Text>
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 4 }}
              gap="20px"
              mb="20px"
              width="100%"
            >
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdBarChart} color={brandColor} />}
                  />
                }
                name="Valor em Vendas"
                value={maskCurrency(data?.amount)}
                growth={percenteProfit}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdBlock} color={brandColor} />}
                  />
                }
                name="Valor bloqueado"
                value={maskCurrency(userData?.amountLocked || 0)}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg={boxBg}
                    icon={<Icon w="32px" h="32px" as={MdAttachMoney} color={brandColor} />}
                  />
                }
                name="Líquido Clientes"
                value={maskCurrency(data?.liquidCustumer)}
              />
            </SimpleGrid>
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 4 }}
              gap="20px"
              mb="20px"
              width="100%"
            >
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg="brand.900"
                    icon={<Icon w="32px" h="32px" as={MdSell} color="white" />}
                  />
                }
                name="Total de vendas"
                value={data?.total}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg="brand.900"
                    icon={<Icon w="28px" h="28px" as={MdTaskAlt} color="white" />}
                  />
                }
                name="Finalizadas"
                value={data?.fineshed}
              />
              <MiniStatistics
                startContent={
                  <IconBox
                    w="56px"
                    h="56px"
                    bg="brand.900"
                    icon={<Icon w="32px" h="32px" as={MdCancel} color="white" />}
                  />
                }
                name="Canceladas"
                value={data?.canceled}
              />
            </SimpleGrid>
          </Box>
        </Flex>

        {/* Vendas no mês e gráfico */}
        <SimpleGrid
          columns={{ base: 1, md: 2, xl: 2 }}
          gap="20px"
          mb="20px"
        >
          <Card alignItems="center" flexDirection="column" w="100%">
            <Flex align="center" w="100%" py="10px">
              <VStack>
                <Text me="auto" px="15px" color={'gray.800'} fontSize="xl" fontWeight="700" lineHeight="100%">
                  Vendas no mês
                </Text>
                <SimpleGrid
                  columns={{ base: 1, md: 2, xl: 2 }}
                  gap="20px"
                  mb="20px"
                  mt={2}
                >
                  <MiniStatistics
                    startContent={
                      <IconBox
                        w="56px"
                        h="56px"
                        bg={boxBg}
                        icon={<Icon w="32px" h="32px" as={BsCreditCardFill} color={brandColor} />}
                      />
                    }
                    name="Cartão de Crédito"
                    sumValue={data?.totalCreditCard}
                    value={data?.creditCard}
                  />
                  <MiniStatistics
                    startContent={
                      <IconBox
                        w="56px"
                        h="56px"
                        mt
                        bg={boxBg}
                        icon={<Icon w="32px" h="32px" as={BsCreditCard} color={brandColor} />}
                      />
                    }
                    name="Cartão de Débito"
                    value={data?.debitCard}
                    sumValue={data?.totalDebitCard}
                  />
                  <MiniStatistics
                    startContent={
                      <IconBox
                        w="56px"
                        h="56px"
                        bg={boxBg}
                        icon={<Icon w="32px" h="32px" as={BsCash} color={brandColor} />}
                      />
                    }
                    name="Voucher"
                    value={data?.voucher}
                    sumValue={data?.totalVoucher}
                  />
                  <MiniStatistics
                    startContent={
                      <IconBox
                        w="56px"
                        h="56px"
                        bg={boxBg}
                        icon={<Icon w="32px" h="32px" as={MdPix} color={brandColor} />}
                      />
                    }
                    name="Pix"
                    value={data?.pix}
                    sumValue={data?.totalPix}
                  />{' '}
                  <MiniStatistics
                    startContent={
                      <IconBox
                        w="56px"
                        h="56px"
                        bg={boxBg}
                        icon={<Icon w="32px" h="32px" as={FaBarcode} color={brandColor} />}
                      />
                    }
                    name="Boleto"
                    value={data?.ticket}
                    sumValue={data?.totalTicket}
                  />
                </SimpleGrid>
              </VStack>
            </Flex>
          </Card>
          <WeeklyRevenue data={data?.daily} />
        </SimpleGrid>
      </Box>
    </PullToRefresh>
  );
}
